import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useNotify, fetchUtils } from 'react-admin';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';

import { normalizeSilaeId } from '../../../helpers';

// Same base/auth as the PostgREST data provider: '/api' is proxied to care and
// the session cookie carries the auth (credentials: 'same-origin'). We call the
// care RPC POST /api/rpc/delete_vivier_fiche the same way the provider issues
// its requests (react-admin fetchUtils.fetchJson), so we inherit the exact
// headers/cookie behaviour without depending on a Bearer token (there is none —
// see authProvider, auth is a same-origin session cookie).
const RPC_URL = 'api/rpc/delete_vivier_fiche';

// "Supprime définitivement la fiche" — hard delete of a vivier fiche created by
// mistake, through the care RPC (which refuses fiches carrying a contract /
// affectation with P0001 vivier_fiche_has_dependencies). Reserved for RH/Admin;
// the real authorization is enforced server-side (the care proxy is fail-closed
// and 403s a non-Admin/Office user before PostgREST), mirroring the Embaucher
// button which also relies on the backend rather than a client-side role gate.
const DeleteFicheDialog = ({ open, onClose, record }) => {
  const notify = useNotify();
  const history = useHistory();
  const [deleting, setDeleting] = useState(false);

  if (!record) {
    return null;
  }

  const matricule = normalizeSilaeId(record.silae_id);
  const composed = [record.firstname, record.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayName = record.fullname || composed || matricule;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { fetchJson } = fetchUtils;
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      await fetchJson(RPC_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ matricule }),
      });
      notify('Fiche supprimée', 'info');
      onClose();
      history.push('/person_vivier');
    } catch (err) {
      // fetchUtils.fetchJson rejects with { status, body, message } on non-2xx.
      const status = err && err.status;
      const body = err && err.body;
      const backendMessage = (body && body.message) || (err && err.message);

      if (status === 403) {
        notify('Action réservée aux RH/Admin.', 'warning');
      } else if (backendMessage === 'vivier_fiche_has_dependencies') {
        notify(
          'Suppression impossible : cette fiche est liée à des contrats ou ' +
            'affectations. Corrigez plutôt son statut.',
          'warning',
        );
      } else if (backendMessage === 'vivier_fiche_invalid_matricule') {
        notify('Suppression impossible : matricule invalide.', 'warning');
      } else {
        notify('Erreur lors de la suppression', 'error');
      }
      // On any error we keep the fiche and close the modal.
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Supprimer la fiche</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {'⚠️ '}
          Supprime <strong>définitivement</strong> la fiche de{' '}
          <strong>{displayName}</strong> dans tout le SI (RH, disponibilités,
          compétences, documents, signalements). Action{' '}
          <strong>irréversible</strong>. À n&apos;utiliser que pour une fiche{' '}
          <strong>créée par erreur</strong>.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Annuler
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          disabled={deleting}
          style={{
            backgroundColor: deleting ? undefined : '#f44336',
            color: '#fff',
          }}
          startIcon={
            deleting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Supprimer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteFicheDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
};

DeleteFicheDialog.defaultProps = {
  record: undefined,
};

export default DeleteFicheDialog;
